<template>
  <Transition name="fade">
    <Button
      v-if="showLeftButton"
      variant="overlay"
      class="button-position left z-10"
      @click="previousBackground"
      :disabled="persistentStore.generatingBackground"
    >
      <template #start>
        <ArrowLeftIcon />
      </template>
      <template #default v-if="persistentStore.generatingBackground && !isMobile">
        <span>{{ I18n.t("views.pagination.previous") }}</span>
      </template>
    </Button>
  </Transition>
  <Transition name="fade">
    <Button
      v-if="showRightButton"
      variant="overlay"
      class="button-position right z-10"
      @click="nextBackground"
      :disabled="persistentStore.generatingBackground"
    >
      <template #end>
        <ArrowRightIcon />
      </template>
      <template #default v-if="persistentStore.generatingBackground && !isMobile">
        <span>{{ I18n.t("views.pagination.next") }}</span>
      </template>
    </Button>
  </Transition>
</template>

<script setup lang="ts">
import { useMagicBackground } from "@/composables/magic_background";
import { useMonitorSize } from "@/composables/monitor_size";
import { useEditorStore } from "@/stores/editor_store";
import { PersistentStore } from "@/stores/persistent_store";
import { Button, ArrowLeftIcon, ArrowRightIcon } from "prism";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";

interface Props {
  persistentStore: PersistentStore;
}

const props = defineProps<Props>();
const persistentStore = computed(() => props.persistentStore);

const { isMobile } = useMonitorSize();
const I18n = inject("I18n");

const { hasNoMagicBackgroundsSelected, isLastBackground, previousBackground, nextBackground } =
  useMagicBackground(persistentStore);

const editorStore = useEditorStore();

const showLeftButton = computed(() => {
  return !hasNoMagicBackgroundsSelected.value || persistentStore.value.generatingBackground;
});

const showRightButton = computed(() => {
  return !isLastBackground.value || persistentStore.value.generatingBackground || hasNoMagicBackgroundsSelected.value;
});

const touchStartX = ref(0);
const touchStartY = ref(0);
const minSwipeDistance = 100;

const handleKeydown = (event: KeyboardEvent) => {
  if (persistentStore.value.generatingBackground) return;

  switch (event.key) {
    case "ArrowLeft":
      if (showLeftButton.value) {
        event.preventDefault();
        previousBackground();
      }
      break;
    case "ArrowRight":
      if (showRightButton.value) {
        event.preventDefault();
        nextBackground();
      }
      break;
  }
};

const handleTouchStart = (event: TouchEvent) => {
  if (!isMobile.value || persistentStore.value.generatingBackground) return;

  const touch = event.touches[0];
  touchStartX.value = touch.clientX;
  touchStartY.value = touch.clientY;
};

const handleTouchEnd = (event: TouchEvent) => {
  if (!isMobile.value || persistentStore.value.generatingBackground) return;

  const touch = event.changedTouches[0];
  const deltaX = touch.clientX - touchStartX.value;
  const deltaY = touch.clientY - touchStartY.value;

  // Check if it's a horizontal swipe (more horizontal than vertical movement)
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
    if (deltaX > 0 && showLeftButton.value) {
      // Swipe right (previous)
      event.preventDefault();
      previousBackground();
    } else if (deltaX < 0 && showRightButton.value) {
      // Swipe left (next)
      event.preventDefault();
      nextBackground();
    }
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);

  if (isMobile.value) {
    window.addEventListener("touchstart", handleTouchStart, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: false });
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("touchstart", handleTouchStart);
  window.removeEventListener("touchend", handleTouchEnd);
});
</script>

<style scoped lang="scss">
.button-position {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  &.left {
    left: 0.5rem;
  }

  &.right {
    right: 0.5rem;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
